import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import IntegrationImage from "../../../../stack/integrations/components/IntegrationImage";
import WidgetWrapper from "../../WidgetWrapper";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import RemediationChart from "./RemediationChart";
import RemediationPerformanceModal from "./RemediationPerformanceModal";

const ToolsRemediationPerformanceWidget = forwardRef(
  ({ mainTimePoint }, ref) => {
    const { getToolsRemediationPerformanceTrend, trackEvent } = useAPI();
    const [showActivityLogTable, setShowActivityLogTable] = useState(false);
    const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
    const colors = useColors();
    const widgetContentRef = useRef(null);

    const colorsChart = [
      colors.primary_200,
      colors.primary_100,
      colors.primary_50,
      colors.primary_25,
      colors.secondary_100,
      colors.secondary_200,
      colors.secondary_300,
      colors.secondary_400,
    ];

    const colorsChart48percent = [
      colors.primary_200_48percent,
      colors.primary_100_48percent,
      colors.primary_50_48percent,
      colors.primary_25_48percent,
      colors.secondary_100_48percent,
      colors.secondary_200_48percent,
      colors.secondary_300_48percent,
      colors.secondary_400_48percent,
    ];

    const {
      data: toolsRemediationPerformanceTrend,
      isLoading: isLoadingToolsRemediationPerformanceTrend,
    } = useQuery({
      queryKey: dashboardKeys.widgetType("toolsRemediationPerformanceTrend"),
      queryFn: getToolsRemediationPerformanceTrend,
      select: (data) => data.data,
    });

    const finalData = toolsRemediationPerformanceTrend;

    useEffect(() => {
      if (finalData && finalData[mainTimePoint]) {
        setBaseTimePoint(mainTimePoint);
      }
    }, [mainTimePoint, finalData]);

    // Sort menu items for the dropdown filter
    const sortMenuItems = createTrendSortMenuItems(
      baseTimePoint,
      setBaseTimePoint,
      finalData,
      icons,
      trackEvent,
      "tools-remediation-performance"
    );

    // All tool remediation performance data with current and base performance
    const allToolRemediationData = useMemo(() => {
      if (!finalData || Object.keys(finalData).length === 0) {
        return [];
      }

      const currentData = finalData[LATEST];
      const baseData =
        baseTimePoint === LATEST ? currentData : finalData[baseTimePoint];

      return Object.entries(currentData)
        .map(([toolName, currentToolData]) => {
          const baseToolData = baseData[toolName] || {
            all: 0,
            HIGH: 0,
            MEDIUM: 0,
            LOW: 0,
          };

          const currentTotal = currentToolData.all || 0;
          const baseTotal = baseToolData.all || 0;
          const performanceChange = currentTotal - baseTotal;
          const percentageChange =
            baseTotal > 0
              ? Math.abs(Math.round((performanceChange / baseTotal) * 100))
              : 0;
          const isPositive = performanceChange >= 0;

          return {
            tool: toolName,
            displayName:
              IntegrationsMapping[toolName]?.shortDisplayName ||
              IntegrationsMapping[toolName]?.displayName ||
              toolName,
            currentTotal: currentTotal,
            baseTotal: baseTotal,
            performanceChange: performanceChange,
            percentageChange: percentageChange,
            isPositive: isPositive,
            high: currentToolData.HIGH || 0,
            medium: currentToolData.MEDIUM || 0,
            low: currentToolData.LOW || 0,
          };
        })
        .filter((tool) => tool.currentTotal > 0) // Only show tools with remediated entities
        .sort((a, b) => b.currentTotal - a.currentTotal);
    }, [finalData, baseTimePoint]);

    // Get top 8 tools by default
    const topTools = useMemo(
      () => allToolRemediationData.slice(0, 8).map((tool) => tool.tool),
      [allToolRemediationData]
    );

    // Initialize selected tools as top 8 tools
    const [selectedTools, setSelectedTools] = useState([]);

    // Update selected tools when top tools change (only if currently empty)
    useEffect(() => {
      if (selectedTools.length === 0 && topTools.length > 0) {
        setSelectedTools(topTools);
      }
    }, [topTools, selectedTools.length]);

    // Filter data based on selected tools
    const toolRemediationData = useMemo(
      () =>
        allToolRemediationData.filter((tool) =>
          selectedTools.includes(tool.tool)
        ),
      [allToolRemediationData, selectedTools]
    );

    const MAX_TOOLS_SELECTED = 8;

    // Selected tools to display in the dropdown
    const selectedToolsToDisplay = {
      title: "Select Tools",
      key: "selected-tools",
      disabled: false,
      onclick: () => {},
      trailingIcon: icons.chevronRightIcon,
      submenuTitle: "Select Tools (Up to 8)",
      submenuMultiple: true,
      submenu: allToolRemediationData.map((option) => ({
        title: (
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            gap={1}
          >
            <IntegrationImage
              name={option.tool?.toLowerCase()}
              width={"12px"}
              newLogo={true}
            />
            <Typography variant="caption">{option.displayName}</Typography>
          </Box>
        ),
        key: option.tool,
        selected: selectedTools.includes(option.tool),
        onclick: () => {
          if (selectedTools.includes(option.tool)) {
            setSelectedTools((prev) =>
              prev.filter((tool) => tool !== option.tool)
            );
          } else {
            if (selectedTools.length < MAX_TOOLS_SELECTED) {
              setSelectedTools((prev) => [...prev, option.tool]);
            }
          }
        },
      })),
    };

    const handleExportToPdf = useCallback(
      async (isCombinedExport = false) => {
        if (
          !widgetContentRef.current ||
          isLoadingToolsRemediationPerformanceTrend
        )
          return null;

        if (!isCombinedExport) {
          trackEvent(
            "User clicked on Export to PDF button in Tools Remediation Performance"
          );
        }

        try {
          if (isCombinedExport) {
            // For combined export, return just the widget content with header
            const widgetContainer = document.createElement("div");

            // Add widget header
            const widgetHeader = createPdfHeader(
              "Remediation Performance Across Tools"
            );
            widgetContainer.appendChild(widgetHeader);

            // Clone and style widget content
            const widgetClone = widgetContentRef.current.cloneNode(true);
            widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

            // Enhance chart visibility for PDF
            const chartElement =
              widgetClone.querySelector(".apexcharts-canvas");
            if (chartElement) {
              chartElement.style.cssText = `
            width: 89% !important;
            height: 350px !important;
          `;
            }

            widgetContainer.appendChild(widgetClone);

            return widgetContainer;
          } else {
            // Individual export - original functionality
            const entirePageElement = await createPdfContainer();

            const header = createPdfHeader(
              "Remediation Performance Across Tools"
            );
            entirePageElement.appendChild(header);

            // Clone and style widget content
            const widgetClone = widgetContentRef.current.cloneNode(true);
            widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

            // Enhance chart visibility for PDF
            const chartElement =
              widgetClone.querySelector(".apexcharts-canvas");
            if (chartElement) {
              chartElement.style.cssText = `
            width: 89% !important;
            height: 350px !important;
          `;
            }

            entirePageElement.appendChild(widgetClone);

            // Measure content dimensions
            const { contentWidth, contentHeight } = await measureContentForPdf(
              entirePageElement
            );

            // Generate PDF
            const pdfConfig = createPdfConfig(
              generateFileName(
                "Breeze_Tools_Remediation_Performance",
                null,
                FILE_EXTENSIONS.PDF
              ),
              contentWidth,
              contentHeight
            );

            await html2pdf().set(pdfConfig).from(entirePageElement).save();
          }
        } catch (error) {
          Toast.error(
            "Failed to export Tools Remediation Performance, Please try again later"
          );
          return null;
        }
      },
      [isLoadingToolsRemediationPerformanceTrend, trackEvent]
    );

    // Expose the export function to parent components
    useImperativeHandle(
      ref,
      () => ({
        exportToPdf: handleExportToPdf,
        widgetId: "toolsRemediationPerformance",
        hasExportCapability: true,
      }),
      [handleExportToPdf]
    );

    return (
      <WidgetWrapper
        title={"Remediation Performance Across Tools"}
        sortMenuItems={{
          data: [...sortMenuItems, selectedToolsToDisplay],
          tooltip: "View changes since selected time",
        }}
        info="Entities affected by remediation efforts in the past 30 days"
        viewAll={{
          handleViewAll: () => {
            setShowActivityLogTable(true);
          },
          tooltip: "View all remediation data",
          newTab: false,
          title: "Full Remediation list",
          hideIcon: true,
        }}
        handleExportToPdf={() => handleExportToPdf(false)}
      >
        {isLoadingToolsRemediationPerformanceTrend ? (
          <LoadingIcon />
        ) : !finalData || Object.keys(finalData).length === 0 ? (
          <Box>
            <Typography variant="caption" marginLeft={2.5}>
              No data available
            </Typography>
          </Box>
        ) : (
          <Box ref={widgetContentRef}>
            <RemediationChart
              toolRemediationData={toolRemediationData}
              baseTimePoint={baseTimePoint}
              colorsChart={colorsChart}
              colorsChart48percent={colorsChart48percent}
            />
            {showActivityLogTable && (
              <RemediationPerformanceModal
                showActivityLogTable={showActivityLogTable}
                setShowActivityLogTable={setShowActivityLogTable}
              />
            )}
          </Box>
        )}
      </WidgetWrapper>
    );
  }
);

ToolsRemediationPerformanceWidget.displayName =
  "ToolsRemediationPerformanceWidget";

export default ToolsRemediationPerformanceWidget;
